import React from 'react';
import { motion } from 'framer-motion';
import { Check, ShieldCheck, CreditCard, Lock, Banknote } from 'lucide-react';
const anim = (d: number = 0) => ({
  initial: {
    opacity: 0,
    y: 16
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  },
  transition: {
    duration: 0.3,
    delay: d,
    ease: 'easeOut' as const
  }
});
export function Pricing() {
  return <section id="planes" className="bg-primary py-12 lg:py-20" data-id="element-135">
      <div className="max-w-[1400px] mx-auto px-5 md:px-12 lg:px-[72px]" data-id="element-136">
        <div className="text-center mb-8 lg:mb-14" data-id="element-137">
          <h2 className="font-serif text-[28px] md:text-[42px] text-text-primary" data-id="element-138">
            Elige cómo empezar
          </h2>
        </div>

        <div className="flex flex-col lg:grid lg:grid-cols-2 gap-5 max-w-[1000px] mx-auto" data-id="element-139">
          {/* Basic Plan */}
          <motion.div {...anim(0)} className="bg-white rounded-[24px] p-6 md:p-10 border border-text-primary/10 shadow-warm flex flex-col" data-id="element-140">
            <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-accent mb-3" data-id="element-141">
              PARA EMPEZAR
            </span>
            <h3 className="font-serif text-[24px] font-medium text-text-primary mb-4" data-id="element-142">
              Plan nutricional
            </h3>

            <div className="mb-1.5 flex items-baseline space-x-2" data-id="element-143">
              <span className="font-serif text-[40px] md:text-[56px] font-semibold text-text-primary leading-none" data-id="element-144">
                $499
              </span>
              <span className="text-[16px] text-text-primary font-medium" data-id="element-145">
                MXN
              </span>
            </div>
            <div className="flex items-center space-x-2 mb-6" data-id="element-146">
              <span className="text-[14px] text-text-secondary line-through opacity-50" data-id="element-147">
                $799
              </span>
              <span className="text-[13px] text-text-secondary" data-id="element-148">
                Pago único
              </span>
            </div>

            <ul className="space-y-3 mb-8 flex-1" data-id="element-149">
              {['Plan de alimentación de 14 días', 'Mini-consulta 15 min con nutriólogo', 'Lista de alimentos recomendados', 'Recetas adaptadas a tus gustos'].map((f, i) => <li key={i} className="flex items-start space-x-2.5" data-id="element-150">
                  <Check className="w-5 h-5 text-accent shrink-0 mt-0.5" data-id="element-151" />
                  <span className="text-[14px] text-text-secondary" data-id="element-152">{f}</span>
                </li>)}
            </ul>

            <motion.button whileTap={{
            scale: 0.96
          }} className="w-full border border-dark text-dark uppercase text-[13px] font-bold tracking-wide py-4 px-8 rounded-full hover:bg-dark hover:text-white transition-all min-h-[52px] mb-6" data-id="element-153">
              Seleccionar este plan
            </motion.button>

            <div className="flex flex-col space-y-2 pt-4 border-t border-text-primary/10" data-id="element-154">
              <p className="text-[12px] text-text-secondary flex items-center" data-id="element-155">
                <span className="mr-2" data-id="element-156">💳</span> Paga con tarjeta, OXXO o SPEI
              </p>
              <p className="text-[12px] text-text-secondary flex items-center" data-id="element-157">
                <span className="mr-2" data-id="element-158">🔒</span> Pago 100% seguro con Stripe
              </p>
            </div>
          </motion.div>

          {/* Premium Plan */}
          <motion.div {...anim(0.1)} className="order-first lg:order-none bg-dark rounded-[24px] p-6 md:p-10 relative flex flex-col shadow-2xl" data-id="element-159">
            <div className="absolute top-5 right-5 bg-accent text-white text-[10px] font-bold uppercase tracking-wider py-1.5 px-3 rounded-full" data-id="element-160">
              MÁS ELEGIDO
            </div>

            <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-accent-secondary mb-3" data-id="element-161">
              PROGRAMA COMPLETO
            </span>
            <h3 className="font-serif text-[24px] font-medium text-primary mb-4" data-id="element-162">
              Acompañamiento mensual
            </h3>

            <div className="mb-1.5 flex items-baseline space-x-2" data-id="element-163">
              <span className="font-serif text-[40px] md:text-[56px] font-semibold text-primary leading-none" data-id="element-164">
                $2,499
              </span>
              <span className="text-[16px] text-primary font-medium" data-id="element-165">
                MXN/mes
              </span>
            </div>
            <div className="flex items-center space-x-2 mb-6" data-id="element-166">
              <span className="text-[14px] text-primary/40 line-through" data-id="element-167">
                $3,499
              </span>
              <span className="text-[13px] text-primary/60" data-id="element-168">
                Compromiso 3 meses
              </span>
            </div>

            <ul className="space-y-3 mb-8 flex-1" data-id="element-169">
              {['4 consultas mensuales de 30 min', 'Plan progresivo con ajustes semanales', 'Seguimiento por WhatsApp', 'Recetas y menús semanales', 'Contenido educativo exclusivo', 'Nutriólogo asignado a tu caso'].map((f, i) => <li key={i} className="flex items-start space-x-2.5" data-id="element-170">
                  <Check className="w-5 h-5 text-accent-secondary shrink-0 mt-0.5" data-id="element-171" />
                  <span className="text-[14px] text-primary/80" data-id="element-172">{f}</span>
                </li>)}
            </ul>

            <motion.button whileTap={{
            scale: 0.96
          }} className="w-full bg-accent text-white uppercase text-[13px] font-bold tracking-wide py-4 px-8 rounded-full hover:bg-accent/90 transition-all min-h-[52px] mb-6" data-id="element-173">
              Empezar el programa
            </motion.button>

            <div className="flex flex-col space-y-2 pt-4 border-t border-primary/10" data-id="element-174">
              <p className="text-[12px] text-primary/60 flex items-center" data-id="element-175">
                <span className="mr-2" data-id="element-176">💳</span> Paga con tarjeta, OXXO o SPEI
              </p>
              <p className="text-[12px] text-primary/60 flex items-center" data-id="element-177">
                <span className="mr-2" data-id="element-178">🔒</span> Pago 100% seguro con Stripe
              </p>
              <p className="text-[12px] text-primary/60 flex items-center" data-id="element-179">
                <span className="mr-2" data-id="element-180">✅</span> Garantía de satisfacción primer
                mes
              </p>
            </div>
          </motion.div>
        </div>

        <motion.div {...anim(0.2)} className="mt-8 text-center max-w-[800px] mx-auto" data-id="element-181">
          <p className="text-[13px] text-text-secondary" data-id="element-182">
            🎓 Todos nuestros nutriólogos tienen cédula profesional expedida por
            la SEP. Puedes verificarla en el registro público.
          </p>
        </motion.div>
      </div>
    </section>;
}